import { ClientPage } from "../../submodules/rapdv/client/elements/PagesCtrl"
import { Basket, DishFeeType, Menu, MenuData } from "../elements/Menu"
import { OrderProximity } from "../elements/OrderProximity"
import { Translation } from "../elements/Translation"
import { PaymentMethod } from "../../server/Types"

export class PageRestaurantOrder implements ClientPage {

  private static readonly TRANSLATION_ID = "PageRestaurantOrder"
  private static readonly DELIVERY = "delivery"
  private static readonly EAT_IN = "table"
  private static readonly STORAGE_KEY_DETAILS = "restaurant-order-details"
  private static readonly PERSISTED_FIELDS = [
    "firstName", "lastName", "phoneNumber", "email",
    "city", "zipCode", "street", "streetNumber", "unitNumber", "floor",
  ]
  private static readonly QUOTE_ADDRESS_FIELDS = ["city", "zipCode", "street", "streetNumber"]
  private static readonly ADDRESS_CARD_FIELDS = ["city", "zipCode", "street", "streetNumber", "unitNumber", "floor"]
  private static readonly LOCATION_TIMEOUT_MS = 15000
  private static readonly LOCATION_MAX_AGE_MS = 30000
  private static readonly DISTANCE_DECIMALS = 1

  private menu: Menu
  private menuData: MenuData
  private basket: Basket
  private deliveryQuote: { verified: boolean; available: boolean; deliveryCost: number } | null = null
  private quotedAddressKey: string = ""
  private addressConfirmed = false
  private locationVerified = false
  private translation = new Translation()
  private t = (key: string): string => this.translation.t(key)

  getPageId = (): string => "page-restaurant-order"

  execute = async (): Promise<void> => {
    const dataEl = document.getElementById("menu-basket-data")
    if (!dataEl) return

    try {
      this.menuData = JSON.parse(dataEl.textContent || "{}")
    } catch (error) {
      return
    }
    if (!this.menuData?.dishes) return

    await this.translation.fetchTranslations(PageRestaurantOrder.TRANSLATION_ID)

    this.menu = new Menu(this.menuData)
    this.basket = this.menu.load()

    this.setupDeliveryMethods()
    this.restoreDetails()
    this.addressConfirmed = this.getQuoteAddress() !== null
    this.renderItems()
    this.updateTotals()
    this.updateDeliveryAddress()
    this.updateEatInSection()
    this.setupFormSubmit()
    this.setupAddressConfirmation()
    if (this.isDeliverySelected() && this.addressConfirmed) {
      await this.fetchDeliveryQuote()
      this.updateTotals()
    }
  }

  private isDeliverySelected = (): boolean =>
    this.getDeliveryMethod() === PageRestaurantOrder.DELIVERY

  private setupAddressConfirmation = (): void => {
    const button = document.querySelector("[data-address-save]") as HTMLButtonElement | null
    button?.addEventListener("click", async () => {
      if (this.addressConfirmed) {
        this.addressConfirmed = false
        this.updateDeliveryAddress()
        this.updateTotals()
        return
      }
      await this.confirmAddress(button)
    })
  }

  private confirmAddress = async (button: HTMLButtonElement): Promise<void> => {
    this.hideUnverifiedAddressMessage()

    const address = this.getQuoteAddress()
    if (!address) {
      this.reportFirstEmptyAddressField()
      return
    }

    button.disabled = true
    await this.fetchDeliveryQuote()
    button.disabled = false

    if (this.deliveryQuote && !this.deliveryQuote.verified) {
      this.showUnverifiedAddressMessage()
      return
    }
    if (this.deliveryQuote?.verified && !this.deliveryQuote.available) {
      this.updateTotals()
      return
    }

    this.addressConfirmed = true
    this.saveDetails()
    this.updateDeliveryAddress()
    this.updateTotals()
  }

  private reportFirstEmptyAddressField = (): void => {
    for (const fieldName of PageRestaurantOrder.QUOTE_ADDRESS_FIELDS) {
      const input = document.querySelector(`[name="${fieldName}"]`) as HTMLInputElement | null
      if (!input?.value?.trim()) {
        input?.reportValidity()
        return
      }
    }
  }

  private showUnverifiedAddressMessage = (): void => {
    document.querySelector("[data-delivery-unverified]")?.classList.remove("d-none")
  }

  private hideUnverifiedAddressMessage = (): void => {
    document.querySelector("[data-delivery-unverified]")?.classList.add("d-none")
  }

  private getQuoteAddress = (): Record<string, string> | null => {
    const address: Record<string, string> = {}
    for (const fieldName of PageRestaurantOrder.QUOTE_ADDRESS_FIELDS) {
      const input = document.querySelector(`[name="${fieldName}"]`) as HTMLInputElement | null
      const value = input?.value?.trim() ?? ""
      if (!value) return null
      address[fieldName] = value
    }
    return address
  }

  private fetchDeliveryQuote = async (): Promise<void> => {
    const address = this.getQuoteAddress()
    if (!address) return

    const addressKey = JSON.stringify(address)
    if (addressKey === this.quotedAddressKey) return

    try {
      const searchParams = new URLSearchParams(address)
      const response = await fetch(`/api/v1/delivery-quote/${this.menuData.storeKey}?${searchParams.toString()}`)
      if (!response.ok) return
      this.deliveryQuote = await response.json()
      this.quotedAddressKey = addressKey
      this.updateTotals()
    } catch (error) {
      // Network error; the server validates again when the order is placed
    }
  }

  onPageClose = (): void => {}

  private getDeliveryMethod = (): string | null => {
    const selected = document.querySelector("[data-delivery-method]:checked") as HTMLInputElement | null
    return selected?.value ?? null
  }

  private hasUnavailableItems = (): boolean =>
    this.basket.items.some((item) => !this.menu.isItemAvailable(item))

  private renderItems = (): void => {
    const itemsEl = document.querySelector("[data-order-summary-items]") as HTMLElement | null
    const emptyEl = document.querySelector("[data-order-empty]")
    const hasItems = this.basket.items.length > 0

    emptyEl?.classList.toggle("d-none", hasItems)
    if (!itemsEl) return

    const unavailableLabel = this.t("(Unavailable)")
    const deliveryMethod = this.getDeliveryMethod()
    const rows: string[] = []
    for (const item of this.basket.items) {
      if (!this.menu.itemExists(item)) continue

      const unitPrice = this.menu.getItemUnitPrice(item)
      const lineTotal = this.menu.getItemLineTotal(item, deliveryMethod)
      const isUnavailable = !this.menu.isItemAvailable(item)
      const namePrefix = isUnavailable ? `${unavailableLabel} ` : ""

      const feesHtml = this.menu.getItemApplicableFees(item, deliveryMethod).map((fee) => {
        const amount = this.menu.getItemFeeLineAmount(item, fee, item.count)
        const label = fee.type === DishFeeType.Percentage ? `${fee.name} ${fee.percentage}%` : fee.name
        return `
        <div class="text-muted small ps-3 pt-1">+ ${label} ${this.menu.formatPrice(amount)}</div>
      `
      }).join("")

      const addonsHtml = this.menu.getItemAddons(item).map((addon) => {
        const amount = this.menu.getAddonLineAmount(addon)
        const quantityLabel = addon.quantity > 1 ? ` ×${addon.quantity}` : ""
        const priceLabel = amount > 0 ? ` ${this.menu.formatPrice(amount)}` : ""
        return `
        <div class="text-muted small ps-3 pt-1">+ ${addon.name}${quantityLabel}${priceLabel}</div>
      `
      }).join("")

      rows.push(`
        <div class="py-2 border-bottom${isUnavailable ? " opacity-50" : ""}">
          <div class="d-flex justify-content-between align-items-center">
            <div class="me-2">
              <div class="fw-semibold">${namePrefix}${this.menu.getItemName(item)}</div>
              <div class="text-muted small">${item.count} × ${this.menu.formatPrice(unitPrice)}</div>
            </div>
            <span class="fw-semibold text-nowrap text-end">${this.menu.formatPrice(lineTotal)}</span>
          </div>
          ${feesHtml}
          ${addonsHtml}
        </div>
      `)
    }
    itemsEl.innerHTML = rows.join("")
  }

  private setupFormSubmit = (): void => {
    const form = document.querySelector("form")
    form?.addEventListener("submit", (event) => {
      if (this.isDeliverySelected() && !this.addressConfirmed) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      if (this.needsLocationCheck() && !this.locationVerified) {
        event.preventDefault()
        event.stopPropagation()
        this.verifyLocation()
        return
      }
      this.saveDetails()
      if (!this.isOnlinePaymentSelected()) {
        localStorage.removeItem(this.menu.getStorageKey())
      }
    })
  }

  private getStoreCoordinates = (): { latitude: number; longitude: number } | null => {
    const form = document.querySelector("form") as HTMLFormElement | null
    const latitude = Number(form?.dataset.storeLatitude ?? "")
    const longitude = Number(form?.dataset.storeLongitude ?? "")
    if (!form?.dataset.storeLatitude || !form?.dataset.storeLongitude) return null
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
    return { latitude, longitude }
  }

  private needsLocationCheck = (): boolean => {
    const maxDistanceKm = OrderProximity.getMaxDistanceKm(this.getDeliveryMethod() ?? "")
    if (maxDistanceKm === null) return false
    if (this.isOnlinePaymentSelected()) return false
    return this.getStoreCoordinates() !== null
  }

  private verifyLocation = (): void => {
    if (!navigator.geolocation) {
      this.showLocationMessage(this.t("We need to confirm that you are at the right restaurant. Please allow location access and try again."), "warning")
      this.restorePlaceOrderButton()
      return
    }
    this.hideLocationMessage()
    navigator.geolocation.getCurrentPosition(this.onLocationSuccess, this.onLocationError, {
      enableHighAccuracy: true,
      timeout: PageRestaurantOrder.LOCATION_TIMEOUT_MS,
      maximumAge: PageRestaurantOrder.LOCATION_MAX_AGE_MS,
    })
  }

  private onLocationSuccess = (position: GeolocationPosition): void => {
    const storeCoordinates = this.getStoreCoordinates()
    const maxDistanceKm = OrderProximity.getMaxDistanceKm(this.getDeliveryMethod() ?? "")

    if (storeCoordinates && maxDistanceKm !== null) {
      const distanceKm = OrderProximity.getDistanceKm(
        position.coords.latitude,
        position.coords.longitude,
        storeCoordinates.latitude,
        storeCoordinates.longitude
      )
      if (distanceKm > maxDistanceKm) {
        const message = this.t("You're {{distance}} km away from the restaurant. You need to be closer to place this order.")
          .replace("{{distance}}", distanceKm.toFixed(PageRestaurantOrder.DISTANCE_DECIMALS))
        this.showLocationMessage(message, "danger")
        this.restorePlaceOrderButton()
        return
      }
    }

    const latitudeInput = document.querySelector("[data-browser-latitude]") as HTMLInputElement | null
    const longitudeInput = document.querySelector("[data-browser-longitude]") as HTMLInputElement | null
    if (latitudeInput) latitudeInput.value = String(position.coords.latitude)
    if (longitudeInput) longitudeInput.value = String(position.coords.longitude)

    this.locationVerified = true
    this.hideLocationMessage()
    this.restorePlaceOrderButton()
    const form = document.querySelector("form") as HTMLFormElement | null
    form?.requestSubmit()
  }

  private onLocationError = (): void => {
    this.showLocationMessage(this.t("We need to confirm that you are at the right restaurant. Please allow location access and try again."), "warning")
    this.restorePlaceOrderButton()
  }

  private restorePlaceOrderButton = (): void => {
    const button = document.querySelector("[data-order-place]") as HTMLButtonElement | null
    if (button) button.type = "submit"
    this.updateTotals()
  }

  private showLocationMessage = (message: string, style: string): void => {
    const messageEl = document.querySelector("[data-location-check-message]") as HTMLElement | null
    if (!messageEl) return
    messageEl.className = `alert alert-${style} mb-3`
    messageEl.textContent = message
  }

  private hideLocationMessage = (): void => {
    const messageEl = document.querySelector("[data-location-check-message]") as HTMLElement | null
    if (messageEl) messageEl.className = "alert mb-3 d-none"
  }

  private isOnlinePaymentSelected = (): boolean => {
    const selected = document.querySelector('input[name="paymentMethod"]:checked') as HTMLInputElement | null
    return selected?.value === PaymentMethod.Online
  }

  private saveDetails = (): void => {
    const form = document.querySelector("form")
    if (!form) return

    const details: Record<string, string> = {}
    for (const fieldName of PageRestaurantOrder.PERSISTED_FIELDS) {
      const input = form.querySelector(`[name="${fieldName}"]`) as HTMLInputElement | null
      details[fieldName] = input?.value ?? ""
    }
    const deliveryMethod = this.getDeliveryMethod()
    if (deliveryMethod) details.deliveryMethod = deliveryMethod

    try {
      localStorage.setItem(PageRestaurantOrder.STORAGE_KEY_DETAILS, JSON.stringify(details))
    } catch (error) {
      // localStorage unavailable (e.g. private mode); ignore
    }
  }

  private restoreDetails = (): void => {
    const form = document.querySelector("form")
    if (!form) return

    let details: Record<string, string>
    try {
      const raw = localStorage.getItem(PageRestaurantOrder.STORAGE_KEY_DETAILS)
      if (!raw) return
      details = JSON.parse(raw)
    } catch (error) {
      return
    }
    if (!details) return

    for (const fieldName of PageRestaurantOrder.PERSISTED_FIELDS) {
      const value = details[fieldName]
      if (value === undefined) continue
      const input = form.querySelector(`[name="${fieldName}"]`) as HTMLInputElement | null
      if (input) input.value = value
    }

    const deliveryMethod = details.deliveryMethod
    if (deliveryMethod) {
      const radio = form.querySelector(`[name="deliveryMethod"][value="${deliveryMethod}"]`) as HTMLInputElement | null
      if (radio) radio.checked = true
    }
  }

  private setupDeliveryMethods = (): void => {
    for (const radio of document.querySelectorAll("[data-delivery-method]")) {
      radio.addEventListener("change", this.refresh)
    }
    for (const radio of document.querySelectorAll("[data-eatin-place]")) {
      radio.addEventListener("change", this.updateEatInSection)
    }
    for (const radio of document.querySelectorAll('input[name="paymentMethod"]')) {
      radio.addEventListener("change", this.refresh)
    }
  }

  private refresh = (): void => {
    this.locationVerified = false
    this.hideLocationMessage()
    this.renderItems()
    this.updateTotals()
    this.updateDeliveryAddress()
    this.updateEatInSection()
    if (this.isDeliverySelected() && this.addressConfirmed) {
      void this.fetchDeliveryQuote()
    }
  }

  private getSelectedEatInPlace = (): HTMLInputElement | null => {
    const places = document.querySelectorAll("[data-eatin-place]")
    for (const place of places) {
      const input = place as HTMLInputElement
      if (input.type !== "radio") return input
      if (input.checked) return input
    }
    return null
  }

  private updateEatInSection = (): void => {
    const isEatIn = this.getDeliveryMethod() === PageRestaurantOrder.EAT_IN
    document.querySelector("[data-eatin-section]")?.classList.toggle("d-none", !isEatIn)

    const selectedPlace = this.getSelectedEatInPlace()
    const needsNumber = isEatIn && selectedPlace?.dataset.needsNumber === "true"

    const numberField = document.querySelector("[data-eatin-number-field]")
    const numberInput = document.querySelector("[data-eatin-number-input]") as HTMLInputElement | null
    const numberLabel = document.querySelector("[data-eatin-number-label]")
    numberField?.classList.toggle("d-none", !needsNumber)
    if (numberInput) numberInput.required = !!needsNumber
    if (needsNumber && numberLabel) numberLabel.textContent = selectedPlace?.dataset.numberLabel ?? ""
    if (needsNumber && numberInput) numberInput.placeholder = selectedPlace?.dataset.numberPlaceholder ?? ""

    const counterInfo = isEatIn && !needsNumber ? (selectedPlace?.dataset.customerInfo ?? "") : ""
    const counterInfoEl = document.querySelector("[data-eatin-counter-info]")
    if (counterInfoEl) {
      counterInfoEl.textContent = counterInfo
      counterInfoEl.classList.toggle("d-none", !counterInfo)
    }
  }

  private updateDeliveryAddress = (): void => {
    const isDelivery = this.isDeliverySelected()
    const addressEl = document.querySelector("[data-delivery-address]")
    addressEl?.classList.toggle("d-none", !isDelivery)
    for (const input of document.querySelectorAll("input[data-required-for-delivery]")) {
      (input as HTMLInputElement).required = isDelivery
    }

    const needsAddressConfirmation = isDelivery && !this.addressConfirmed
    document.querySelector("[data-order-rest]")?.classList.toggle("d-none", needsAddressConfirmation)
    if (!needsAddressConfirmation) this.hideUnverifiedAddressMessage()

    const button = document.querySelector("[data-address-save]") as HTMLButtonElement | null
    if (button) {
      button.textContent = this.addressConfirmed ? button.dataset.labelChange ?? "" : button.dataset.labelSave ?? ""
      button.className = this.addressConfirmed ? "btn btn-outline-secondary" : "btn btn-primary"
    }

    for (const fieldName of PageRestaurantOrder.ADDRESS_CARD_FIELDS) {
      const input = document.querySelector(`[name="${fieldName}"]`) as HTMLInputElement | null
      if (input) input.readOnly = this.addressConfirmed
    }
  }

  private updateTotals = (): void => {
    const deliveryMethod = this.getDeliveryMethod()
    const isDelivery = deliveryMethod === PageRestaurantOrder.DELIVERY
    const hasItems = this.basket.items.length > 0
    const subtotal = this.menu.getSubtotal(this.basket, deliveryMethod)
    const isDeliveryUnavailable = isDelivery && !!this.deliveryQuote?.verified && !this.deliveryQuote?.available
    const deliveryCost = isDelivery && this.deliveryQuote?.available ? this.deliveryQuote.deliveryCost : 0
    const total = subtotal + deliveryCost

    const subtotalEl = document.querySelector("[data-order-subtotal]")
    if (subtotalEl) subtotalEl.textContent = this.menu.formatPrice(subtotal)

    const deliveryRow = document.querySelector("[data-order-delivery-row]")
    const deliveryEl = document.querySelector("[data-order-delivery]")
    deliveryRow?.classList.toggle("d-none", deliveryCost <= 0)
    if (deliveryEl) deliveryEl.textContent = this.menu.formatPrice(deliveryCost)

    const totalEl = document.querySelector("[data-order-total]")
    if (totalEl) totalEl.textContent = this.menu.formatPrice(total)

    document.querySelector("[data-delivery-unavailable]")?.classList.toggle("d-none", !isDeliveryUnavailable)

    const isStoreOpen = this.menuData.isStoreOpen !== false
    const placeBtn = document.querySelector("[data-order-place]") as HTMLButtonElement | null
    if (placeBtn) placeBtn.disabled = !hasItems || this.hasUnavailableItems() || !isStoreOpen || isDeliveryUnavailable

    const basketInput = document.querySelector("[data-order-basket-input]") as HTMLInputElement | null
    if (basketInput) basketInput.value = JSON.stringify(this.basket)
  }
}
